import React from "react";
import {
  IWSModalContext,
  WSBadgeProps,
  WSIconName,
  WSImageProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { TextList } from "../components/TextList/TextList.module";
import { createHealthSherpaLink, getPathForBenefit } from "../utils";
import { Link } from "react-router-dom";
import { INewUser } from "@wingspanhq/users/dist/lib/interfaces";
import { IMember } from "@wingspanhq/users";
import { openInNewTab } from "../../utils/openInNewTab";
import {
  BENEFITS_ADD_ZIP_CODE_MODAL,
  BenefitsAddZipCode
} from "../modals/BenefitsAddZipCode";

import {
  BenefitBlockProps,
  BenefitBlockStatus
} from "../components/BenefitBlock/BenefitBlock.module";
import { IFeatureFlags } from "../../query/hooks/useFeatureFlags";
import HealthInsuranceImage from "../assets/HealthInsuranceImage.png";

export interface IBenefitFaq {
  question: React.ReactNode;
  answer: React.ReactNode;
}

export interface IBenefitProviderContact {
  phone?: string;
  email?: string;
}

export interface IBenefitCTAWidget {
  headerText?: string;
  logo?: unknown;
  company?: string;
  transitionLogo?: unknown;
  usageText?: React.ReactNode;
  videoScript?: React.ReactNode;
  ctaBtnText: React.ReactNode;
  ctaLink: string;
  ctaBtnFooter: React.ReactNode;
  contactInfo?: IBenefitProviderContact;
  memberNumber?: string;
  cost?: string;
  status: BenefitBlockStatus;
  isPremium?: boolean;
}

export interface IBenefitCard {
  icon: WSIconName;
  description: React.ReactNode;
}
export interface IBenefitCardSection {
  title?: React.ReactNode;
  cards: Array<IBenefitCard>;
}

export interface IBenefitLineItem {
  description: React.ReactNode;
  amount: number;
}
export interface IBenefitCostExample {
  title: React.ReactNode;
  lineItems: Array<IBenefitLineItem>;
  finalCost: IBenefitLineItem;
}
export interface IBenefitTab {
  title: React.ReactNode;
  routePath: string;
  description: React.ReactNode;
  videoScript?: React.ReactNode;
  cardSection?: IBenefitCardSection;
  costExample?: IBenefitCostExample;
}
export interface IBenefitDetails {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  gettingStarted: IBenefitCTAWidget;
  tabs: Array<IBenefitTab>;
  faqs?: Array<IBenefitFaq>;
}
export interface BenefitData extends BenefitBlockProps {
  ctaImage?: WSImageProps;
  details?: (
    user: INewUser,
    member: IMember,
    features: IFeatureFlags,
    openModal: IWSModalContext["openModal"]
  ) => React.ReactNode;
  detailsJson?: IBenefitDetails;
  primaryButton?: (
    user: INewUser,
    member: IMember,
    features: IFeatureFlags,
    openModal: IWSModalContext["openModal"]
  ) => {
    label: string;
    link?: string;
    onClick?(): void;
    permanent?: boolean;
    modal?: React.ReactNode;
  };
  secondaryButton?: (
    user: INewUser,
    member: IMember,
    features: IFeatureFlags,
    openModal: IWSModalContext["openModal"]
  ) => {
    label: string;
    link?: string;
    onClick?(): void;
  };
  skipActivation?: boolean;
  hideUserData?: boolean;
  linkMore?: string;
}

export enum BenefitCardStatus {
  INCLUDED = "INCLUDED",
  DEFAULT = "DEFAULT",
  PREMIUM = "PREMIUM",
  COMING_SOON = "COMING_SOON"
}

export const BenefitsBadgeProps: { [key: string]: WSBadgeProps | undefined } = {
  [BenefitCardStatus.DEFAULT]: undefined,
  [BenefitCardStatus.COMING_SOON]: {
    text: "Coming soon",
    backgroundColor: "grey200",
    textProps: {
      color: "grey600"
    }
  },
  [BenefitCardStatus.INCLUDED]: {
    text: "Included",
    backgroundColor: "green100",
    textProps: {
      color: "green500"
    }
  },
  [BenefitCardStatus.PREMIUM]: {
    text: "Premium",
    textProps: { color: "grey600" },
    leftIcon: { name: "lock", color: "grey500" },
    backgroundColor: "grey200"
  }
};

export const _BENEFITS = [
  {
    ctaImage: {
      placeholder: "Health insurance logos",
      src: HealthInsuranceImage
    },
    title: "Health Insurance",
    status: BenefitBlockStatus.DEFAULT,
    cost: "Varied",
    icon: "health",
    description:
      "Helping you find, enroll in, and make the best use of your health insurance plan.",
    details: (user: INewUser, member: IMember, featureFlags: IFeatureFlags) => {
      return (
        <>
          <BenefitsAddZipCode />
          <WSText.Heading3>
            Calculate your most affordable health plan
          </WSText.Heading3>
          <TextList
            mt="M"
            list={[
              "See what savings you qualify for",
              "Compare quotes from multiple insurance companies",
              "We'll calculate the most affordable plan for your needs"
            ]}
          />
          <WSText.Heading3 mt="M">Need help?</WSText.Heading3>
          <WSText.Paragraph mt="M">
            Give us a call at <a href="tel:833-945-1760">(833) 945-1760</a>, and
            we'll help answer any questions or help you get enrolled!
          </WSText.Paragraph>
        </>
      );
    },
    hideUserData: true,
    skipActivation: true,
    primaryButton: (
      user: INewUser,
      member: IMember,
      featureFlags: IFeatureFlags,
      openModal: (name: string) => void
    ) => {
      const zipCode = member.profile.address?.postalCode as string;

      if (!zipCode) {
        return {
          permanent: true,
          label: "Get covered",
          onClick() {
            openModal(BENEFITS_ADD_ZIP_CODE_MODAL);
          }
        };
      }

      return {
        permanent: true,
        label: "Get covered",
        onClick() {
          openInNewTab(createHealthSherpaLink(zipCode));
        }
      };
    }
  },
  {
    title: "Pet Insurance",
    icon: "pet",
    status: BenefitCardStatus.COMING_SOON,
    description: (
      <>
        We’re still working on the best solution for you. In the meantime, check
        out{" "}
        <span onClick={e => e.stopPropagation()}>
          <Link to={getPathForBenefit("Pet Care", "my-benefits")}>
            pet care
          </Link>
        </span>
        .
      </>
    )
  },
  {
    title: "Therapy",
    status: BenefitBlockStatus.COMING_SOON,
    icon: "life",
    description: "We’re still working on the best solution for you."
  },
  {
    title: "Life Insurance",
    status: BenefitBlockStatus.COMING_SOON,
    icon: "life",
    description: "We’re still working on the best solution for you."
  },
  {
    title: "Business Liability Insurance",
    status: BenefitBlockStatus.COMING_SOON,
    icon: "business",
    description: "We’re still working on the best solution for you."
  },
  {
    title: "Retirement Plans",
    status: BenefitBlockStatus.COMING_SOON,
    icon: "yacht",
    description: "We’re still working on the best solution for you."
  }
] as const;

export const BENEFITS = (_BENEFITS as any) as BenefitData[];
